import React, { useState, useEffect } from "react";
import axios from "axios";
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import ReactDatetime from "react-datetime";
import { jwtDecode } from "jwt-decode";
import { isAuthenticated } from "utils/auth";

// reactstrap components
import {
  Container, Row, Col, Card, CardHeader, CardBody, CardTitle, CardText,
  Spinner, Form, FormGroup, Label, Input,
  Button, Alert, InputGroup, InputGroupAddon, InputGroupText, UncontrolledTooltip // Added Tooltip
} from "reactstrap";

// core components
import IndexNavbar from "components/Navbars/IndexNavbar.js";
import CalendarPageHeader from "components/Headers/CalendarPageHeader.js";
import IndexFooter from "components/Footers/IndexFooter.js";

// API URLs
const ANNOUNCEMENT_API_URL = "https://api.pembrokefarmersmarket.ca/api/announcements"; // Base events endpoint

function Announcement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVendor, setIsVendor] = useState(false); // Added Vendor state
  const [userId, setUserId] = useState(null); // Added state for logged-in user ID
  const [newEventData, setNewEventData] = useState({ name: '', date: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false); // For Add Event form
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [actionLoading, setActionLoading] = useState(null); // Tracks ID of event being acted upon (join/leave/delete)

  // --- Check User Role and ID ---
  useEffect(() => {
    let role = null;
    let id = null;
    if (isAuthenticated()) {
      const token = localStorage.getItem("token");
      try {
        const decoded = jwtDecode(token);
        if (decoded.user) {
          role = decoded.user.role;
          id = decoded.user.id;
          setUserId(id); // Store user ID
        }
      } catch (e) {
        console.error("Error decoding token:", e);
      }
    }
    setIsAdmin(role === 'admin');
    setIsVendor(!!id && role !== 'admin'); // Is a vendor if logged in and NOT admin
  }, []);

  // Fetch events from the API
  const fetchAnnouncements = async () => {
    // Keep loading spinner for main list if desired, or remove setIsLoading(true) here
    // setLoading(true);
    setError(null);
    try {
      const config = {};
      const token = localStorage.getItem("token");
       // Send token if logged in, backend might populate attendee status based on it
      if (token) {
           config.headers = { 'Authorization': `Bearer ${token}` };
      }

      const res = await axios.get(ANNOUNCEMENT_API_URL, config); // Fetch events
      const parsedEvents = res.data.map(event => ({
        ...event,
        dateObj: moment(event.date).isValid() ? moment(event.date).toDate() : null,
        // Ensure attendees is always an array, even if missing from response
        attendees: Array.isArray(event.attendees) ? event.attendees : []
      })).filter(event => event.dateObj !== null);
      setEvents(parsedEvents);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Could not load announcements. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Fetch on mount

  // Update upcoming events list (remains the same)
  useEffect(() => {
    const today = moment().startOf('day');
    const futureEvents = events
      .filter(event => moment(event.dateObj).isSameOrAfter(today))
      .sort((a, b) => a.dateObj - b.dateObj);
    setUpcomingEvents(futureEvents);
  }, [events]);

  // Add/remove body class (remains the same)
  useEffect(() => {
    document.body.classList.add("profile-page");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    return function cleanup() {
      document.body.classList.remove("profile-page");
    };
  }, []);

  // --- Add Event Form Handlers (remain the same) ---
  const handleNewEventChange = (e) => setNewEventData({ ...newEventData, [e.target.name]: e.target.value });
  const handleDateChange = (momentDate) => {
      if (moment(momentDate).isValid()) setNewEventData({ ...newEventData, date: moment(momentDate).toDate() });
      else console.warn("Invalid date selected");
  };
  const handleAddEventSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFeedback({ message: '', type: '' });
    const token = localStorage.getItem("token");
    if (!token || !isAdmin) { /* Rest of validation */ return; }
    if (!newEventData.name || !newEventData.date) { /* Rest of validation */ return; }

    try {
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const payload = { ...newEventData, date: moment(newEventData.date).toISOString() };
        await axios.post(ANNOUNCEMENT_API_URL, payload, config);
        setFeedback({ message: 'Event added successfully!', type: 'success' });
        setNewEventData({ name: '', date: '', description: '' });
        fetchAnnouncements();
    } catch (err) {
        console.error("Error adding event:", err.response?.data || err);
        const errMsg = err.response?.data?.message || "Could not add event.";
        setFeedback({ message: `Error: ${errMsg}`, type: 'danger' });
    } finally {
        setIsSubmitting(false);
    }
  };

  // --- Event Action Handlers ---

  const handleDeleteEvent = async (eventId) => {
    if (!isAdmin) return;
    if (window.confirm('Are you sure you want to delete this event?')) {
        setActionLoading(eventId); // Indicate loading for this specific event
        setFeedback({ message: '', type: '' });
        const token = localStorage.getItem("token");
        if (!token) { /* Handle missing token */ setActionLoading(null); return; }

        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        try {
            await axios.delete(`${ANNOUNCEMENT_API_URL}/${eventId}`, config);
            setFeedback({ message: 'Event deleted successfully!', type: 'success' });
            fetchAnnouncements(); // Refresh the list
        } catch (err) {
            console.error("Error deleting event:", err.response?.data || err);
            const errMsg = err.response?.data?.message || 'Could not delete event.';
            setFeedback({ message: `Error: ${errMsg}`, type: 'danger' });
        } finally {
            setActionLoading(null); // Stop loading indicator
        }
    }
  };

  const handleJoinLeaveEvent = async (eventId, isAttending) => {
      if (!isVendor && !isAdmin) return; // Allow admins to join/leave too? Or just vendors? Adjust as needed.
      setActionLoading(eventId);
      setFeedback({ message: '', type: '' });
      const token = localStorage.getItem("token");
      if (!token) { /* Handle missing token */ setActionLoading(null); return; }

      // Determine API endpoint based on action
      const action = isAttending ? 'unattend' : 'attend';
      const JOIN_LEAVE_API_URL = `${ANNOUNCEMENT_API_URL}/${eventId}/${action}`; // e.g., /api/events/123/attend

      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const method = action === 'attend' ? 'post' : 'delete'; // Use POST to attend, DELETE to unattend

      try {
          // Using axios[method] requires payload even for delete, send empty object
          await axios[method](JOIN_LEAVE_API_URL, {}, config);
          setFeedback({ message: `Successfully ${isAttending ? 'left' : 'joined'} event!`, type: 'success' });
          fetchAnnouncements(); // Refresh list to update attendee status and button text
      } catch (err) {
           console.error(`Error ${action}ing event:`, err.response?.data || err);
          const errMsg = err.response?.data?.message || `Could not ${action} event.`;
          setFeedback({ message: `Error: ${errMsg}`, type: 'danger' });
      } finally {
          setActionLoading(null);
      }
  };


  // --- Render ---
  return (
    <>
      <IndexNavbar />
      <CalendarPageHeader />
      <br />
      <div className="main">
        <div className="section">
          <Container>
            <Row>
              <Col md="12">
                <h2 className="title text-center">Announcement</h2>
                {/* Global Feedback Area */}
                {feedback.message && !isSubmitting && actionLoading === null && ( // Hide global feedback during specific actions
                    <Alert color={feedback.type} isOpen={!!feedback.message} toggle={() => setFeedback({ message: '', type: '' })}>
                        {feedback.message}
                    </Alert>
                )}
              </Col>
            </Row>
            <Row>
              {/* Calendar & Add Event Form Column */}
              <Col md="7" lg="8">
              <Card>
                  <CardBody>
                    <CardTitle tag="h4" className="text-center">Announcement</CardTitle>
                    <hr />
                    {loading && <div className="text-center"><Spinner color="primary" /> Loading...</div>}
                    {error && <p className="text-danger text-center">{error}</p>}
                    {!loading && !error && (
                      upcomingEvents.length > 0 ? (
                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          {upcomingEvents.map(event => {
                             const isAttending = isVendor && event.attendees?.includes(userId);
                             const isLoadingAction = actionLoading === event._id;

                             return (
                                <div key={event._id} className="mb-2 pb-2 border-bottom event-list-item"> {/* Added border-bottom */}
                                  {/* Use Reactstrap Row/Col for better control */}
                                  <Row className="align-items-center no-gutters"> {/* align-items-center for vertical centering */}
                                    {/* Event Info Column (takes remaining space) */}
                                    <Col style={{ paddingRight: '10px' }}> {/* Added padding-right */}
                                        <h6>{event.name}</h6>
                                        <CardText className="mb-1"> {/* Reduced margin */}
                                            <small className="text-muted">
                                            {moment(event.dateObj).format("MMMM Do YYYY, h:mm a")} {/* Changed format slightly */}
                                            </small>
                                        </CardText>
                                        {event.description && (
                                            <CardText className="text-muted small mb-0"> {/* Smaller text for description */}
                                                {event.description}
                                            </CardText>
                                        )}
                                    </Col>

                                    {/* Actions Column (takes auto width) */}
                                    <Col xs="auto" className="d-flex align-items-center" style={{ gap: '5px' }}> {/* Keep flex row and gap */}

                                        {/* Vendor Join/Leave Button */}
                                        {isVendor && (
                                            <Button
                                                className="btn-round" // Kept btn-round
                                                color={isAttending ? "warning" : "success"}
                                                size="sm"
                                                onClick={() => handleJoinLeaveEvent(event._id, isAttending)}
                                                disabled={isLoadingAction}
                                                id={`join-leave-${event._id}`}
                                                style={{ padding: '0.3rem 0.6rem', minWidth: '65px' }} // Adjusted padding and min-width
                                            >
                                                {isLoadingAction ? <Spinner size="sm" color="light" /> : (isAttending ? 'Leave' : 'Join')}
                                            </Button>
                                        )}

                                         {/* Admin Delete Button */}
                                        {isAdmin && (
                                            <Button
                                                className="btn-round btn-icon" // Icon only
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleDeleteEvent(event._id)}
                                                disabled={isLoadingAction}
                                                id={`delete-${event._id}`}
                                                style={{ padding: '0.3rem 0.5rem' }} // Adjusted padding
                                            >
                                                {isLoadingAction ? <Spinner size="sm" color="light" /> : <i className="fa fa-trash"/>}
                                            </Button>
                                        )}

                                        {/* Tooltips remain the same */}
                                        {isVendor && <UncontrolledTooltip placement="top" target={`join-leave-${event._id}`} delay={0}>{isAttending ? 'Leave this Event' : 'Join this Event'}</UncontrolledTooltip>}
                                        {isAdmin && <UncontrolledTooltip placement="top" target={`delete-${event._id}`} delay={0}>Delete Event</UncontrolledTooltip>}
                                    </Col>
                                  </Row>
                                  {/* Removed the <hr/> inside the loop, using border-bottom on the container div */}
                                </div>
                              );
                           })}
                        </div>
                      ) : (
                        <p className="text-center text-muted">No upcoming events found.</p>
                      )
                    )}
                  </CardBody>
                </Card>
                {/* Admin Add Event Form */}
                {isAdmin && (
                  <Card className="mt-4">
                     <CardHeader><CardTitle tag="h4">Add New Event</CardTitle></CardHeader>
                    <CardBody>
                       {/* Form-specific feedback can be shown here if needed */}
                       {isSubmitting && feedback.message && (
                           <Alert color={feedback.type}>{feedback.message}</Alert>
                       )}
                      <Form onSubmit={handleAddEventSubmit}>
                         {/* FormGroups for name, date, description */}
                         <FormGroup>
                            <Label for="eventName">Event Name</Label>
                            <Input id="eventName" name="name" placeholder="Event Title" value={newEventData.name} onChange={handleNewEventChange} required disabled={isSubmitting}/>
                          </FormGroup>
                          <FormGroup>
                              <Label for="eventDate">Event Date & Time</Label>
                              <InputGroup className="date" id="datetimepicker">
                                <ReactDatetime inputProps={{ placeholder: "Select Date and Time", id: "eventDate", name: "date", disabled: isSubmitting }} value={newEventData.date} onChange={handleDateChange} timeFormat={true}/>
                                <InputGroupAddon addonType="append"><InputGroupText><span className="glyphicon glyphicon-calendar"><i aria-hidden={true} className="fa fa-calendar" /></span></InputGroupText></InputGroupAddon>
                              </InputGroup>
                          </FormGroup>
                          <FormGroup>
                            <Label for="eventDescription">Description (Optional)</Label>
                            <Input id="eventDescription" name="description" type="textarea" placeholder="Details..." value={newEventData.description} onChange={handleNewEventChange} rows="3" disabled={isSubmitting}/>
                          </FormGroup>
                        <Button className="btn-round" color="primary" type="submit" disabled={isSubmitting}>
                          {isSubmitting ? <><Spinner size="sm" /> Adding...</> : 'Add Event'}
                        </Button>
                      </Form>
                    </CardBody>
                  </Card>
                )}
              </Col>

              {/* Upcoming Events Column */}
              <Col md="5" lg="4">
                <Card>
                  <CardBody>
                    <CardTitle tag="h4" className="text-center">Upcoming Events</CardTitle>
                    <hr />
                    {loading && <div className="text-center"><Spinner color="primary" /> Loading...</div>}
                    {error && <p className="text-danger text-center">{error}</p>}
                    {!loading && !error && (
                      upcomingEvents.length > 0 ? (
                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          {upcomingEvents.map(event => {
                             const isAttending = isVendor && event.attendees?.includes(userId);
                             const isLoadingAction = actionLoading === event._id;

                             return (
                                <div key={event._id} className="mb-2 pb-2 border-bottom event-list-item"> {/* Added border-bottom */}
                                  {/* Use Reactstrap Row/Col for better control */}
                                  <Row className="align-items-center no-gutters"> {/* align-items-center for vertical centering */}
                                    {/* Event Info Column (takes remaining space) */}
                                    <Col style={{ paddingRight: '10px' }}> {/* Added padding-right */}
                                        <h6>{event.name}</h6>
                                        <CardText className="mb-1"> {/* Reduced margin */}
                                            <small className="text-muted">
                                            {moment(event.dateObj).format("MMMM Do YYYY, h:mm a")} {/* Changed format slightly */}
                                            </small>
                                        </CardText>
                                        {event.description && (
                                            <CardText className="text-muted small mb-0"> {/* Smaller text for description */}
                                                {event.description}
                                            </CardText>
                                        )}
                                    </Col>

                                    {/* Actions Column (takes auto width) */}
                                    <Col xs="auto" className="d-flex align-items-center" style={{ gap: '5px' }}> {/* Keep flex row and gap */}

                                        {/* Vendor Join/Leave Button */}
                                        {isVendor && (
                                            <Button
                                                className="btn-round" // Kept btn-round
                                                color={isAttending ? "warning" : "success"}
                                                size="sm"
                                                onClick={() => handleJoinLeaveEvent(event._id, isAttending)}
                                                disabled={isLoadingAction}
                                                id={`join-leave-${event._id}`}
                                                style={{ padding: '0.3rem 0.6rem', minWidth: '65px' }} // Adjusted padding and min-width
                                            >
                                                {isLoadingAction ? <Spinner size="sm" color="light" /> : (isAttending ? 'Leave' : 'Join')}
                                            </Button>
                                        )}

                                         {/* Admin Delete Button */}
                                        {isAdmin && (
                                            <Button
                                                className="btn-round btn-icon" // Icon only
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleDeleteEvent(event._id)}
                                                disabled={isLoadingAction}
                                                id={`delete-${event._id}`}
                                                style={{ padding: '0.3rem 0.5rem' }} // Adjusted padding
                                            >
                                                {isLoadingAction ? <Spinner size="sm" color="light" /> : <i className="fa fa-trash"/>}
                                            </Button>
                                        )}

                                        {/* Tooltips remain the same */}
                                        {isVendor && <UncontrolledTooltip placement="top" target={`join-leave-${event._id}`} delay={0}>{isAttending ? 'Leave this Event' : 'Join this Event'}</UncontrolledTooltip>}
                                        {isAdmin && <UncontrolledTooltip placement="top" target={`delete-${event._id}`} delay={0}>Delete Event</UncontrolledTooltip>}
                                    </Col>
                                  </Row>
                                  {/* Removed the <hr/> inside the loop, using border-bottom on the container div */}
                                </div>
                              );
                           })}
                        </div>
                      ) : (
                        <p className="text-center text-muted">No upcoming events found.</p>
                      )
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <IndexFooter />
      <style>{`
        /* Styles from previous step remain */
        .react-calendar { width: 100% !important; max-width: none !important; border: none; border-radius: 12px; box-shadow: 0 6px 10px -4px rgba(0, 0, 0, 0.15); font-family: 'Montserrat', "Helvetica", Arial, sans-serif; }
        .react-calendar__tile--active { background: #51cbce !important; color: white !important; }
        .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus { background-color: #f0f0f0; }
        .react-calendar__navigation button:enabled:hover, .react-calendar__navigation button:enabled:focus { background-color: #e6e6e6; }
        .event-marker { position: relative; }
        .event-marker::after { content: ''; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 5px; height: 5px; background-color: #f5593d; border-radius: 50%; }
        .react-calendar__tile--active.event-marker::after { background-color: white; }
        .rdt { width: 100%; }
        .rdt .form-control { border-top-right-radius: 0; border-bottom-right-radius: 0; }
         /* Ensure action buttons in list don't wrap unnecessarily */
         .event-list-item .btn { white-space: nowrap; }
         .event-list-item .row > .col, .event-list-item .row > [class*="col-"] { padding-right: 5px; padding-left: 5px; } /* Reduce padding */
         .event-list-item .pl-0 { padding-left: 0 !important; }
      `}</style>
    </>
  );
}

export default Announcement;